import { type ReactNode, useState } from 'react';
import {
  ChevronDown,
  Map as MapIcon,
  MoreVertical,
  AlertCircle,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { WorkspaceNameBadge } from '../LayersTab/LayerWorkspaceBadge';

type SitStatLayerListShellProps = {
  title: ReactNode;
  /** Replaces the title while the row is expanded. */
  nameEditor?: ReactNode;
  workspaceName?: string | null;
  checked: boolean;
  indeterminate?: boolean;
  onCheckedChange: (checked: boolean) => void;
  children?: ReactNode;
  defaultOpen?: boolean;
  showArcGisBadge?: boolean;
  /** Agency or platform serving the data, e.g. "ArcGIS", "USDA". */
  provenanceBadge?: string;
  error?: string;
  onZoom?: () => void;
  zoomDisabled?: boolean;
  zoomLabel?: string;
  onViewDetails?: () => void;
  onFocusView?: () => void;
  onPermissions?: () => void;
  onPublish?: () => void;
  onArchive?: () => void;
  onApprove?: () => void;
  publishLabel?: string;
  publishDisabled?: boolean;
  className?: string;
  contentClassName?: string;
  /** `data-tour` on the layer card for product tours. */
  dataTour?: string;
  /** `data-tour` on the zoom control for product tours. */
  zoomTour?: string;
  /** `data-tour` on the provenance badge for product tours. */
  qualityTour?: string;
};

export function SitStatLayerListShell({
  title,
  nameEditor,
  workspaceName,
  checked,
  indeterminate = false,
  onCheckedChange,
  children,
  defaultOpen = false,
  showArcGisBadge = false,
  provenanceBadge,
  error,
  onZoom,
  zoomDisabled = false,
  zoomLabel = 'Zoom to layer',
  onViewDetails,
  onFocusView,
  onPermissions,
  onPublish,
  onArchive,
  onApprove,
  publishLabel = 'Publish',
  publishDisabled,
  className,
  contentClassName,
  dataTour,
  zoomTour,
  qualityTour,
}: SitStatLayerListShellProps) {
  const [open, setOpen] = useState(defaultOpen);
  const hasBody = children != null;
  const badgeLabel = provenanceBadge ?? 'ArcGIS';

  return (
    <Card
      size="sm"
      data-tour={dataTour}
      className={cn(
        'tw:gap-0 tw:!border tw:!border-solid tw:py-0 tw:shadow-sm',
        open && hasBody ? 'tw:!border-primary' : 'tw:!border-transparent',
        error && 'tw:ring-destructive/40',
        className
      )}
    >
      <Collapsible open={hasBody ? open : false} onOpenChange={setOpen}>
        <div className="tw:flex tw:items-center tw:gap-1.5 tw:px-2.5 tw:py-2">
          <Checkbox
            checked={checked}
            indeterminate={indeterminate}
            onCheckedChange={(value) => {
              onCheckedChange(value === true);
            }}
            onClick={(event) => event.stopPropagation()}
            aria-label={
              typeof title === 'string'
                ? `Toggle ${title}`
                : 'Toggle visibility'
            }
            className="tw:shrink-0"
          />

          {hasBody && open && nameEditor ? (
            <div className="tw:flex tw:min-w-0 tw:flex-1 tw:items-center tw:gap-1.5 tw:px-1">
              {nameEditor}
              {workspaceName ? (
                <WorkspaceNameBadge name={workspaceName} />
              ) : null}
              <button
                type="button"
                className="tw:ml-auto tw:rounded-md tw:p-0.5 tw:text-muted-foreground tw:hover:bg-accent/60"
                aria-label="Collapse layer"
                onClick={() => setOpen(false)}
              >
                <ChevronDown className="tw:h-3.5 tw:w-3.5 tw:rotate-180" />
              </button>
            </div>
          ) : hasBody ? (
            <CollapsibleTrigger
              render={
                <button
                  type="button"
                  className="tw:flex tw:min-w-0 tw:flex-1 tw:items-center tw:justify-start tw:gap-1.5 tw:rounded-md tw:px-1 tw:py-0.5 tw:text-left tw:hover:bg-accent/60"
                >
                  <span className="tw:min-w-0 tw:truncate tw:text-xs tw:font-semibold">
                    {title}
                  </span>
                  {workspaceName ? (
                    <WorkspaceNameBadge name={workspaceName} />
                  ) : null}
                  <ChevronDown
                    className={cn(
                      'tw:ml-auto tw:h-3.5 tw:w-3.5 tw:shrink-0 tw:text-muted-foreground tw:transition-transform',
                      open && 'tw:rotate-180'
                    )}
                  />
                </button>
              }
            />
          ) : (
            <span className="tw:flex tw:min-w-0 tw:flex-1 tw:items-center tw:gap-1 tw:px-1">
              <span className="tw:min-w-0 tw:truncate tw:text-xs tw:font-semibold">
                {title}
              </span>
              {workspaceName ? (
                <WorkspaceNameBadge name={workspaceName} />
              ) : null}
            </span>
          )}

          <div className="tw:ml-auto tw:flex tw:shrink-0 tw:items-center tw:gap-0.5">
            {showArcGisBadge ? (
              <Badge
                variant="outline"
                data-tour={qualityTour}
                className={cn(
                  'tw:h-5 tw:px-1.5 tw:text-[10px]',
                  badgeLabel === 'ArcGIS'
                    ? 'tw:border-emerald-500/50 tw:text-emerald-400'
                    : 'tw:border-sky-500/50 tw:text-sky-400'
                )}
              >
                {badgeLabel}
              </Badge>
            ) : null}

            {error ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-xs"
                        className="tw:text-destructive"
                        aria-label="Layer error"
                      >
                        <AlertCircle className="tw:h-3.5 tw:w-3.5" />
                      </Button>
                    }
                  />
                  <TooltipContent className="dark tw:max-w-xs">
                    {error}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : null}

            {onZoom ? (
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                disabled={zoomDisabled}
                aria-label={zoomLabel}
                title={zoomLabel}
                data-tour={zoomTour}
                onClick={(event) => {
                  event.stopPropagation();
                  onZoom();
                }}
              >
                <MapIcon className="tw:h-3.5 tw:w-3.5" />
              </Button>
            ) : null}

            {onViewDetails ||
            onFocusView ||
            onPermissions ||
            onPublish ||
            onArchive ||
            onApprove ? (
              <DropdownMenu>
                <DropdownMenuTrigger
                  render={
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-xs"
                      aria-label="Layer actions"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <MoreVertical className="tw:h-3.5 tw:w-3.5" />
                    </Button>
                  }
                />
                <DropdownMenuContent align="end" className="dark tw:min-w-44">
                  {onFocusView ? (
                    <DropdownMenuItem
                      onClick={(event) => {
                        event.stopPropagation();
                        onFocusView();
                      }}
                    >
                      Focused View
                    </DropdownMenuItem>
                  ) : null}
                  {onPermissions ? (
                    <DropdownMenuItem
                      onClick={(event) => {
                        event.stopPropagation();
                        onPermissions();
                      }}
                    >
                      Permissions
                    </DropdownMenuItem>
                  ) : null}
                  {onViewDetails ? (
                    <DropdownMenuItem
                      onClick={(event) => {
                        event.stopPropagation();
                        onViewDetails();
                      }}
                    >
                      View details
                    </DropdownMenuItem>
                  ) : null}
                  {onPublish ? (
                    <DropdownMenuItem
                      disabled={publishDisabled}
                      onClick={(event) => {
                        event.stopPropagation();
                        onPublish();
                      }}
                    >
                      {publishLabel}
                    </DropdownMenuItem>
                  ) : null}
                  {onApprove ? (
                    <DropdownMenuItem
                      onClick={(event) => {
                        event.stopPropagation();
                        onApprove();
                      }}
                    >
                      Approve
                    </DropdownMenuItem>
                  ) : null}
                  {onArchive ? (
                    <DropdownMenuItem
                      onClick={(event) => {
                        event.stopPropagation();
                        onArchive();
                      }}
                    >
                      Archive
                    </DropdownMenuItem>
                  ) : null}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </div>
        </div>

        {hasBody ? (
          <CollapsibleContent
            className={cn(
              'tw:space-y-2 tw:border-t tw:px-2.5 tw:py-2',
              contentClassName
            )}
          >
            {children}
          </CollapsibleContent>
        ) : null}
      </Collapsible>
    </Card>
  );
}
